import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// Api
import api from '../api/Base';
import InstanceApi from '../api/instance/InstanceApi';

// Types
import Action from '../types/Action';
import InstanceTarget from '../types/instance/InstanceTarget';
import InstanceTargetHost from '../types/instance/InstanceTargetHost';
import {
    InstanceFormData,
    InstanceFormState,
    InstanceFormValidation,
    createDefaultFormData,
    createDefaultValidation,
    createDefaultUIState,
    createDefaultDependencies
} from '../types/instance/InstanceFormState';

// Utils
import { InstanceFormValidator, InstanceFormUtils } from '../helpers/InstanceFormValidation';
import Helper from '../helpers/Helper';
import { showError } from '../utils/showAlert';

// Constants
import { ACTIONS } from '../component/Constants';

interface UseInstanceFormProps {
    instances: InstanceTarget[];
    setInstances: (instances: InstanceTarget[]) => void;
    eventTypes: any[];
}

export const useInstanceForm = ({ instances, setInstances, eventTypes }: UseInstanceFormProps) => {
    const navigate = useNavigate();
    const { actionId, instanceId } = useParams();

    // Main state
    const [formState, setFormState] = useState<InstanceFormState>({
        formData: createDefaultFormData(),
        validation: createDefaultValidation(),
        uiState: createDefaultUIState(actionId === ACTIONS.DELETE.type),
        dependencies: createDefaultDependencies()
    });

    const [action, setAction] = useState<Action>(ACTIONS.CREATE);

    // Update form data
    const updateFormData = useCallback((updates: Partial<InstanceFormData>) => {
        setFormState(prev => ({
            ...prev,
            formData: { ...prev.formData, ...updates }
        }));
    }, []);

    // Update validation
    const updateValidation = useCallback((updates: Partial<InstanceFormValidation>) => {
        setFormState(prev => ({
            ...prev,
            validation: { ...prev.validation, ...updates }
        }));
    }, []);

    // Update UI state
    const updateUIState = useCallback((updates: Partial<typeof formState.uiState>) => {
        setFormState(prev => ({
            ...prev,
            uiState: { ...prev.uiState, ...updates }
        }));
    }, []);

    // Update dependencies
    const updateDependencies = useCallback((updates: Partial<typeof formState.dependencies>) => {
        setFormState(prev => ({
            ...prev,
            dependencies: { ...prev.dependencies, ...updates }
        }));
    }, []);

    // Load instance types and default values
    const loadInstanceTypes = useCallback(async () => {
        try {
            const entityType = await InstanceApi.getInstanceTypes();
            updateDependencies({ defaultValues: entityType });
        } catch (error: any) {
            console.error('Failed to load instance types:', error);
            const errorMessage = error.message || error.toString();
            showError('Failed to load instance types', errorMessage);
        }
    }, [updateDependencies]);

    // Load agents
    const loadAgents = useCallback(async () => {
        try {
            const response = await api.get('agent');
            if (response?.data) {
                updateDependencies({ agents: response.data });
            }
        } catch (error: any) {
            console.error('Failed to load agents:', error);
            const errorMessage = error.message || error.toString();
            showError('Failed to load agents', errorMessage);
        }
    }, [updateDependencies]);

    // Load third party links
    const loadThirdPartyLinks = useCallback(async () => {
        if (!instanceId) return;

        try {
            const response = await InstanceApi.getInstanceLinks(Number(instanceId));
            if (response?.length) {
                const currentInstanceLinks = response.filter(
                    (item: any) => item.datasourceid === Number(instanceId)
                );
                updateDependencies({ initialLinks: JSON.parse(JSON.stringify(currentInstanceLinks)) });
                updateFormData({ links: JSON.parse(JSON.stringify(currentInstanceLinks)) });
            }
        } catch (error: any) {
            console.error('Failed to load third party links:', error);
            const errorMessage = error.message || error.toString();
            showError('Failed to load third party links', errorMessage);
        }
    }, [instanceId, updateDependencies, updateFormData]);

    // Load instance tags
    const loadInstanceTags = useCallback(async () => {
        try {
            const allTags = await InstanceApi.getAllTags();
            if (allTags?.length) {
                updateDependencies({ allTags });
                
                if (instanceId) {
                    const currentInstanceTags = allTags.filter(
                        (item: any) => item.datasourceid === Number(instanceId)
                    );
                    updateDependencies({ initialTags: JSON.parse(JSON.stringify(currentInstanceTags)) });
                    updateFormData({ tags: JSON.parse(JSON.stringify(currentInstanceTags)) });
                }
            }
        } catch (error: any) {
            console.error('Failed to load instance tags:', error);
            const errorMessage = error.message || error.toString();
            showError('Failed to load instance tags', errorMessage);
        }
    }, [instanceId, updateDependencies, updateFormData]);

    // Load mapped hosts
    const loadMappedHosts = useCallback(async (instanceId: number) => {
        try {
            const hostMappings = await InstanceApi.getInstanceHostByInstance(instanceId);
            const mappedHostIds = hostMappings.map((host: InstanceTargetHost) => host.hostid);
            updateFormData({ mappedHosts: mappedHostIds });
        } catch (error: any) {
            console.error('Failed to retrieve mapped instances.', error);
            const errorMessage = error.message || error.toString();
            showError('Failed to retrieve mapped hosts', errorMessage);
        }
    }, [updateFormData]);

    // Load instance data
    const loadInstance = useCallback((instanceId: number) => {
        const instance = instances.find((inst: InstanceTarget) => inst.id === instanceId);
        
        if (!instance) {
            updateUIState({ isMissing: true });
            return;
        }

        updateFormData({
            id: instance.id,
            name: instance.name,
            host: instance.host,
            type: instance.type,
            agentId: instance.agentid,
            database: instance.database,
            server: instance.server,
            monitor: instance.monitor,
            debug: instance.debug,
            port: instance.port,
            username: instance.username,
            password: instance.password === null ? '' : instance.password,
            driver: instance.driver,
            url: instance.url,
            advanced: instance.useurl,
            parameters: instance.parameters,
            monitoringInterval: instance.monitoringintervalmilliseconds,
            batchInterval: instance.batchintervalseconds || 60,
            detectChanges: instance.detectchanges,
            objectStatistics: instance.collectobjectstatistics || false,
            collectSqlStatistics: instance.collectsqlstatistics,
            collectDeadlocks: instance.collectdeadlocks || false,
            collectTransactionContention: instance.collecttransactioncontention || false,
            collectTempdb: instance.collecttempdb || false,
            collectJobHistory: instance.collectjobhistory || false,
            explainLimit: instance.explainlimit ?? 20,
            encrypted: instance.encrypted || false,
            collectBlockingSessions: instance.type === 'cockroachdb' ? false : instance.collectblockingsessions || false
        });
    }, [instances, updateFormData, updateUIState]);

    // Set action based on actionId
    const setActionFromId = useCallback(() => {
        switch (actionId) {
            case 'create':
                setAction(ACTIONS.CREATE);
                break;
            case 'edit':
                setAction(ACTIONS.UPDATE);
                break;
            case 'delete':
                setAction(ACTIONS.DELETE);
                updateUIState({ disabled: true });
                break;
            default:
                console.error('Unknown action requested', actionId);
                break;
        }
    }, [actionId, updateUIState]);

    // Validation handlers
    const validateName = useCallback(() => {
        const validation = InstanceFormValidator.validateName(
            formState.formData.name,
            formState.formData.id,
            instances
        );
        
        updateValidation({ name: validation });
        updateFormData({ name: formState.formData.name.trim() });
    }, [formState.formData.name, formState.formData.id, instances, updateValidation, updateFormData]);

    const validateHost = useCallback(() => {
        const validation = InstanceFormValidator.validateHost(
            formState.formData.host
        );
        
        updateValidation({ host: validation });
        updateFormData({ host: formState.formData.host.trim() });
    }, [formState.formData.host, updateValidation, updateFormData]);

    const validatePort = useCallback(() => {
        const validation = InstanceFormValidator.validatePort(formState.formData.port);
        updateValidation({ port: validation });
    }, [formState.formData.port, updateValidation]);

    const validateDatabase = useCallback(() => {
        const validation = InstanceFormValidator.validateDatabase(
            formState.formData.database,
            formState.formData.type
        );
        updateValidation({ database: validation });
        updateFormData({ database: formState.formData.database.trim() });
    }, [formState.formData.database, formState.formData.type, updateValidation, updateFormData]);

    // Submit handler
    const handleSubmit = useCallback(async () => {
        updateUIState({ loading: true });

        // Validate the complete form
        const { isValid, validation, updatedLinks, updatedTags } = InstanceFormValidator.validateCompleteForm(
            formState.formData,
            instances
        );

        updateValidation(validation);
        updateFormData({ links: updatedLinks, tags: updatedTags });

        if (!isValid) {
            updateUIState({ loading: false });
            return;
        }

        try {
            const data = [{
                id: formState.formData.id,
                name: formState.formData.name,
                agentid: formState.formData.agentId,
                type: formState.formData.type,
                database: formState.formData.database,
                server: formState.formData.server,
                debug: formState.formData.debug,
                host: formState.formData.host,
                port: Number(formState.formData.port),
                username: formState.formData.username,
                password: formState.formData.password === '' ? null : formState.formData.password,
                passwordchanged: formState.formData.passwordChanged,
                driver: formState.formData.driver,
                monitor: formState.formData.monitor,
                url: formState.formData.url,
                useurl: formState.formData.advanced,
                parameters: formState.formData.parameters,
                monitoringintervalmilliseconds: formState.formData.monitoringInterval,
                batchintervalseconds: formState.formData.batchInterval,
                detectchanges: formState.formData.detectChanges,
                explainlimit: formState.formData.explainLimit,
                collectsqlstatistics: InstanceFormUtils.hasStatisticsCollection(formState.formData.type) && formState.formData.collectSqlStatistics,
                collectdeadlocks: (formState.formData.type === 'oracle' || formState.formData.type === 'sqlserver') ? formState.formData.collectDeadlocks : false,
                collectblockingsessions: formState.formData.collectBlockingSessions,
                collectobjectstatistics: formState.formData.objectStatistics,
                collecttransactioncontention: formState.formData.collectTransactionContention,
                collecttempdb: formState.formData.collectTempdb,
                collectjobhistory: formState.formData.collectJobHistory,
                encrypted: formState.formData.encrypted
            }];

            let result;
            const thirdPartiesArray = updatedLinks.map(({ validName, validUrl, id, ...rest }) => rest);
            const tagsArray = updatedTags.map(({ validName, validValue, id, ...rest }) => rest);

            switch (action) {
                case ACTIONS.CREATE:
                    result = await api.post('datasource', data);
                    if (result.status === 200) {
                        let updatedInstances = await InstanceApi.getInstances();
                        updatedInstances = updatedInstances.filter((instance: InstanceTarget) => instance.name === formState.formData.name);
                        const createdId = updatedInstances[0].id;

                        // Add third party links
                        thirdPartiesArray.forEach(link => { link.datasourceid = createdId; });
                        await api.post(`datasource/link?datasourceid=${createdId}`, thirdPartiesArray);

                        // Add tags
                        tagsArray.forEach(tag => { tag.datasourceid = createdId; });
                        await api.post(`datasource/tag?datasourceid=${createdId}`, tagsArray);

                        // Create host mappings
                        const instanceTargetHosts: InstanceTargetHost[] = formState.formData.mappedHosts.map(hostId => ({
                            id: createdId,
                            hostid: hostId
                        }));
                        await api.post('datasource/host', instanceTargetHosts);

                        Helper.createInstanceEvent(eventTypes, createdId, `${action.title}d Instance`, `${action.title}d monitored instance in DBmarlin`);
                    }
                    break;

                case ACTIONS.UPDATE:
                    result = await api.put('datasource', data);

                    // Update links if changed
                    if (JSON.stringify(formState.dependencies.initialLinks) !== JSON.stringify(updatedLinks)) {
                        if (formState.dependencies.initialLinks.length) {
                            const arrayOfIds = formState.dependencies.initialLinks.map((obj: { id: any; }) => ({ id: obj.id }));
                            await api.delete('datasource/link', { data: arrayOfIds });
                        }
                        await api.post('datasource/link', thirdPartiesArray);
                    }

                    // Update tags if changed
                    if (JSON.stringify(formState.dependencies.initialTags) !== JSON.stringify(updatedTags)) {
                        if (formState.dependencies.initialTags.length) {
                            const arrayOfIds = formState.dependencies.initialTags.map((obj: { id: any; }) => ({ id: obj.id }));
                            await api.delete('datasource/tag', { data: arrayOfIds });
                        }
                        await api.post('datasource/tag', tagsArray);
                    }

                    // Update host mappings
                    await api.delete('datasource/host', { data: [{ 'id': formState.formData.id }] });
                    const instanceTargetHosts: InstanceTargetHost[] = formState.formData.mappedHosts.map(hostId => ({
                        id: formState.formData.id,
                        hostid: hostId
                    }));
                    await api.post('datasource/host', instanceTargetHosts);

                    Helper.createInstanceEvent(eventTypes, formState.formData.id, `${action.title}d Instance`, `${action.title}d monitored instance in DBmarlin`);
                    break;

                case ACTIONS.DELETE:
                    // Delete host associations
                    await api.delete('datasource/host', { data: [{ 'id': formState.formData.id }] });

                    // Delete links and tags
                    if (formState.dependencies.initialLinks.length) {
                        await api.delete('datasource/link', { data: formState.dependencies.initialLinks });
                    }
                    if (formState.dependencies.initialTags.length) {
                        await api.delete('datasource/tag', { data: formState.dependencies.initialTags });
                    }

                    // Delete the instance
                    result = await api.delete('datasource', { data });
                    break;
            }

            // Refresh instances and navigate back
            const updatedInstances = await InstanceApi.getInstances();
            setInstances(updatedInstances);
            navigate(-1);

        } catch (error: any) {
            console.error('Failed to complete requested action', action, error);
            const errMsg = error.response?.data?.message || error.message || error.toString();
            const errorHeading = `Failed to ${action.title.toLowerCase()} instance`;
            showError(errorHeading, errMsg);
            updateUIState({ loading: false });
        }
    }, [formState, instances, action, eventTypes, setInstances, navigate, updateValidation, updateFormData]);

    // Initialize form
    useEffect(() => {
        loadInstanceTypes();
        loadAgents();
        setActionFromId();
        
        if (instanceId) {
            loadInstance(Number(instanceId));
            loadMappedHosts(Number(instanceId));
            loadThirdPartyLinks();
        }
        loadInstanceTags();
    }, [instanceId, instances, actionId]);

    return {
        formState,
        action,
        updateFormData,
        updateValidation,
        updateUIState,
        validateName,
        validateHost,
        validatePort,
        validateDatabase,
        handleSubmit,
        // Utility functions
        requireDatabase: (type: string) => InstanceFormUtils.requireDatabase(type),
        requireServer: (type: string) => InstanceFormUtils.requireServer(type),
        hasStatisticsCollection: (type: string) => InstanceFormUtils.hasStatisticsCollection(type),
        canViewPassword: (passwordChanged: boolean) => InstanceFormUtils.canViewPassword(
            action === ACTIONS.CREATE,
            action === ACTIONS.UPDATE,
            passwordChanged
        ),
        getDefaultCollectBlockingSessions: (type: string) => InstanceFormUtils.getDefaultCollectBlockingSessions(
            type,
            formState.dependencies.defaultValues
        )
    };
};
